import { StowagePositionDto } from '@planning/app/api'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { MRT_ColumnFiltersState, MRT_FilterOption, MRT_TableInstance } from 'material-react-table'
import { action, computed, makeObservable, observable } from 'mobx'

export interface OrderListTableColumnsDto {
  id: number
  number?: string | null
  archetype?: string | null
  isoCode?: string | null
  category?: string | null
  shippingLine?: string | null
  forwarder?: string | null
  cargoStatus?: string | null
  grossWeight: number
  imoClass: string
  oog: string
  cooling: string
  hold: string[]
  status?: string | null
  approved: string

  containerTare?: number | null
  notes?: string | null
  operationalInstructions?: string | null

  originalOrder: IOrderItem
  originPosition?: StowagePositionDto | null
  actualPosition?: StowagePositionDto | null

  containerJourneyId?: number | null

  numericReleaseReference?: string | null
  nonNumericReleaseReference?: string | null
}

export interface RestowListTableColumnsDto extends OrderListTableColumnsDto {
  destinationPosition?: StowagePositionDto | null
}

export interface LoadListTableColumnsDto
  extends OrderListTableColumnsDto, RestowListTableColumnsDto {
  shipper?: string | null
  pod?: string | null
  fnd?: string | null
  vgm?: number | null
  weightClass?: string | null
  bookingNo?: string | null
  soNumber?: string | null
  stowageInstructions?: string | null
}

export interface DischargeListTableColumnsDto extends OrderListTableColumnsDto {
  consignee?: string | null
  pol?: string | null
  sealNo?: string | null
  documentNo?: string | null
  bl?: string | null
  inlandDest?: string | null
}

export interface IFilterDefinition {
  columnKey: string
  value: unknown
  filterType: MRT_FilterOption
}

export interface IVesselVisitOrderListViewStore<T extends OrderListTableColumnsDto> {
  isFullView: boolean
  columnVisibility: Partial<Record<keyof DischargeListTableColumnsDto, boolean>>

  setIsFullView(table: MRT_TableInstance<T>, value: boolean): void
  changeColumnVisibility(table?: MRT_TableInstance<T>): void
  get ordersTableData(): T[]
}

export interface GeneralCargoOrderListTableColumnsDto {
  id: number
  commodity?: string | null
  referenceNumber?: string | null
  lotNumber?: string | null
  status?: string | null
  customer?: string | null
  package?: string | null
  plannedQuantity?: number | null
  actualQuantity?: number | null
  weight?: number | null
  weightUnit?: string | null
  imoClass: string
  railTrackName?: string | null
  railcarName?: string | null
}

export abstract class VesselVisitOrderListViewStore<
  T extends OrderListTableColumnsDto,
> implements IVesselVisitOrderListViewStore<T> {
  vesselVisitItem: IVesselVisitItem
  isFullView = false
  columnVisibility: Partial<Record<keyof T, boolean>> = {}
  columnFilters: MRT_ColumnFiltersState = []

  constructor(vesselVisitItem: IVesselVisitItem) {
    makeObservable(this, {
      isFullView: observable,
      columnVisibility: observable,
      columnFilters: observable,
      vesselVisitItem: observable,

      setIsFullView: action,

      ordersTableData: computed,
      generalCargoOrdersTableData: computed,
    })

    this.vesselVisitItem = vesselVisitItem
  }

  setIsFullView(table: MRT_TableInstance<T>, value: boolean) {
    this.isFullView = value
    this.changeColumnVisibility(table)
  }

  // TODO: UT mapping
  mapToTableData(order: IOrderItem): OrderListTableColumnsDto {
    return {
      id: order.id,
      number: order.data.containerNumber,
      isoCode: order.data.containerIsoCode,
      archetype: order.data.isoCodeArchetype ?? order.data.nonNumericArchetype,
      category: order.journeyCategory,
      shippingLine: order.data.customerName,
      forwarder: order.forwarder?.data.name,
      cargoStatus: order.data.isEmpty ? 'Empty' : 'Full',
      grossWeight: order.data.grossWeight ?? 0,
      imoClass:
        Array.isArray(order.data.imoClasses) && order.data.imoClasses.length > 0
          ? order.data.imoClasses.join(', ')
          : 'No',
      oog: order.data.isOOG ? 'Yes' : 'No',
      cooling: order.containerJourney?.data.coolingOrderId ? 'Yes' : 'No',
      hold: order.activeHoldNames,
      status: order.containerJourney?.status,
      approved: order.data.isApproved ? 'Yes' : 'No',

      containerTare: order.data.containerTare ?? 0,
      notes: order.data.notes,
      operationalInstructions: order.data.operationalInstructions,
      actualPosition: order.data.actualStowagePosition,

      containerJourneyId: order.containerJourney?.data.id,

      numericReleaseReference: order.data.numericReleaseReference,
      nonNumericReleaseReference: order.data.nonNumericReleaseReference,

      originalOrder: order,
    }
  }

  mapGeneralCargoToTableData(order: IOrderItem): GeneralCargoOrderListTableColumnsDto {
    return {
      id: order.id,
      commodity: order.data.commodityName,
      referenceNumber: order.data.referenceNumber,
      lotNumber: order.data.lotNumber,
      status: order.data.status,
      customer: order.data.customerName,
      package: order.data.packageName,
      plannedQuantity: order.data.plannedCargoAmount,
      actualQuantity: order.data.actualCargoAmount,
      weight: order.data.grossWeight,
      weightUnit: order.data.weightUnit,
      imoClass:
        Array.isArray(order.data.imoClasses) && order.data.imoClasses.length > 0
          ? order.data.imoClasses.join(', ')
          : 'No',
    }
  }

  abstract changeColumnVisibility(table?: MRT_TableInstance<T>): void
  abstract get ordersTableData(): T[]
  abstract get generalCargoOrdersTableData(): GeneralCargoOrderListTableColumnsDto[]
}

export class VesselVisitDischargeListViewStore extends VesselVisitOrderListViewStore<DischargeListTableColumnsDto> {
  changeColumnVisibility(table?: MRT_TableInstance<DischargeListTableColumnsDto>) {
    if (this.isFullView) {
      table?.setColumnVisibility({})
    } else {
      table?.setColumnVisibility({
        consignee: false,
        forwarder: false,
        inlandDest: false,
        documentNo: false,
        bl: false,
        pol: false,
        inboundPos: false,
      })
    }
  }

  mapToTableData(order: IOrderItem): DischargeListTableColumnsDto {
    return {
      ...super.mapToTableData(order),

      consignee: order.data.consignee,
      pol: order.data.portOfLoading,
      sealNo:
        Array.isArray(order.data.seals) && order.data.seals.length > 0
          ? order.data.seals
              .map(s => s?.number ?? '')
              .filter(Boolean)
              .join(', ')
          : order.data.hasSeals
            ? 'Yes'
            : 'No',
      documentNo: order.data.releaseReference,
      bl: order.data.billOfLading,
      originPosition: order.data.stowagePosition,
      inlandDest: order.data.inlandDestination,
    }
  }

  get ordersTableData(): DischargeListTableColumnsDto[] {
    return (
      this.vesselVisitItem?.discharge?.containerOrders.map(order => this.mapToTableData(order)) ??
      []
    )
  }

  get generalCargoOrdersTableData(): GeneralCargoOrderListTableColumnsDto[] {
    return (
      this.vesselVisitItem?.discharge?.generalCargoOrders.map(order =>
        super.mapGeneralCargoToTableData(order),
      ) ?? []
    )
  }
}

export class VesselVisitLoadListViewStore extends VesselVisitOrderListViewStore<LoadListTableColumnsDto> {
  changeColumnVisibility(table?: MRT_TableInstance<LoadListTableColumnsDto>) {
    if (this.isFullView) {
      table?.setColumnVisibility({})
    } else {
      table?.setColumnVisibility({
        category: false,
        shipper: false,
        portNo: false,
        forwarder: false,
        pod: false,
        fnd: false,
        soNumber: false,
        weightClass: false,
        bookingNo: false,
        importExportRestows: false,
        outboundPos: false,
        actualPos: false,
        stowageInstructions: false,
      })
    }
  }

  mapToTableData(order: IOrderItem): LoadListTableColumnsDto {
    return {
      ...super.mapToTableData(order),

      shipper: order.shipper?.data.name,
      pod: order.data.portOfDischarge,
      fnd: order.data.finalDestination,
      vgm: order.data.vgm ?? 0,
      weightClass: order.weightClass?.name,
      bookingNo: order.data.referenceNumber,
      soNumber: order.data.shippingOrderReference,
      destinationPosition: order.data.stowagePosition,
      stowageInstructions: order.data.stowageInstruction?.name,
    }
  }

  get ordersTableData(): LoadListTableColumnsDto[] {
    return (
      this.vesselVisitItem?.load?.containerOrders.map(order => this.mapToTableData(order)) ?? []
    )
  }

  get generalCargoOrdersTableData(): GeneralCargoOrderListTableColumnsDto[] {
    return (
      this.vesselVisitItem?.load?.generalCargoOrders.map(order =>
        super.mapGeneralCargoToTableData(order),
      ) ?? []
    )
  }
}

export class VesselVisitRestowListViewStore extends VesselVisitOrderListViewStore<RestowListTableColumnsDto> {
  changeColumnVisibility() {
    return
  }

  mapToTableData(order: IOrderItem): RestowListTableColumnsDto {
    const outboundOrder = order.containerJourney?.outboundOrder

    return {
      ...super.mapToTableData(order),
      originPosition: order.data.stowagePosition,
      destinationPosition: outboundOrder?.data.stowagePosition,
      actualPosition: outboundOrder?.data.actualStowagePosition,
    }
  }

  get ordersTableData(): RestowListTableColumnsDto[] {
    return (
      this.vesselVisitItem?.restow.dischargeOrders.map(order => this.mapToTableData(order)) ?? []
    )
  }

  get generalCargoOrdersTableData(): GeneralCargoOrderListTableColumnsDto[] {
    return []
  }
}
